/* eslint-disable no-restricted-syntax */
/* eslint-disable max-lines */
/* eslint-disable no-plusplus */
import React from 'react';
import {
  filter,
  some,
  template as lodashTemplate,
  templateSettings,
} from 'lodash';
import type { TFunction } from 'i18next';
import { Money } from '@app/currency';
import {
  Vertical,
  Customer,
  PortfolioItem,
  LegacyProduct,
  LegacyChildProduct,
} from '@app/types';

import { VERTICALS } from '@app/config';

class Products {
  // used in OrderSuccess page / OrderReview page / LegacyProduct breadcrumbs
  static insertContentStackTemplates(
    template: string,
    product: Nullable<LegacyProduct | LegacyChildProduct> = null,
    customer: Nullable<Customer> = null,
    portfolioItem: Nullable<PortfolioItem> = null,
  ) {
    let filledString = template;
    const sources = {
      product,
      customer,
      portfolioItem,
    };

    templateSettings.interpolate = /{{([\s\S]+?)}}/g;
    const compiled = lodashTemplate(filledString);
    filledString = compiled(sources);

    return filledString;
  }

  /**
   * for nested object interpolations in content-stack
   * this was needed because the above method insertContentStackTemplates
   * tanks out the page if the anything is undefined
   * @param template is a string with {{product.shipping.daysToShip}}
   * @param obj needs to be sent in keyed with its variable name
   * ex for obj: const product = { shipping: { daysToShip: 2 } };
   * interpolate(template, { product }); -> see variable name is key
   * @param currency the users selected currency
   * */
  static interpolateContentStack(
    template: string,
    obj: Record<string, any>,
    currency: string | null = null,
  ) {
    if (template) {
      const regEx = /\{{(.+?)\}}/g;
      // this is callback for the js method replace and the args follow that callback's signature
      const replacer = (...args: string[]) => {
        const keys = args[1].split('.');
        let replacement = '';

        // this is pretty gross, but gets the job done for now...
        for (let i = 0; i < keys.length; i++) {
          if (obj[keys[i]]) {
            replacement = obj[keys[i]];
          } else if (replacement && replacement[keys[i]]) {
            if (keys[i] === 'localAmount') {
              replacement = Money.getDollars(
                replacement[keys[i]],
                false,
                true,
                currency,
              );
            } else if (keys[i] === 'localTotal') {
              replacement = Money.getDollars(
                replacement[keys[i]],
                true,
                false,
                currency,
              );
            } else {
              replacement = replacement[keys[i]];
            }
          } else {
            replacement = '';
            break;
          }
        }

        return replacement;
      };

      return template.replace(regEx, replacer);
    }
  }

  /**
   * Used pretty exhaustively throughout codebase. Of note:
   * LegacyProduct seems to have this key on the product object,
   * but it does not exist on the data we fetch from the graph
   */
  static isRaffle(product?: LegacyProduct | LegacyChildProduct | undefined) {
    return product?.meta?.raffle || product?.listingType === 'raffle';
  }

  // Same comment as isRaffle
  static isRestock(product?: LegacyProduct | LegacyChildProduct | undefined) {
    return product?.meta?.restock || product?.listingType === 'restock';
  }

  // Same comment as isRaffle
  static isCharity(product?: LegacyProduct | LegacyChildProduct | undefined) {
    return product?.meta?.charity || product?.listingType === 'charity';
  }

  // Same comment as isRaffle
  static isDropX(product?: LegacyProduct | LegacyChildProduct | undefined) {
    return product?.listingType === 'dropx';
  }

  // Used in Portfolio / Following
  static getSizeLabel(
    title: string | null,
    size: string | null,
    sizeTitle: string | null,
    translator: TFunction,
  ): JSX.Element | string | null {
    const endings = {
      '(GS)': translator('U.S. Grade School Sizes'),
      '(Y)': translator('U.S. Youth Sizes'),
      '(W)': translator("U.S. Women's Size"),
      '(I)': translator('U.S. Infant Sizes'),
      '(PS)': translator('U.S. Preschool Sizes'),
    };

    let label = `${sizeTitle}`;

    switch (sizeTitle) {
      case "Us M Men's Size":
      case "U.S. Men's Size":
        label = translator("U.S. Men's Size");
        break;
      case 'Grade':
        label = translator('pdp_grade_label');
        break;
      default:
        label = `${sizeTitle}`;
    }

    if (label.endsWith(':')) {
      label = label.slice(0, -1);
    }

    for (const e in endings) {
      if (title?.endsWith(e)) {
        label = endings[e];
      }
    }

    if (!size) {
      return null;
    }

    return (
      <span style={{ display: 'inline' }}>
        <span style={{ display: 'inline' }}>
          {label}: {size?.toString()}
        </span>
      </span>
    );
  }

  // Used in useProduct CHK hook / AddPortfolio
  static getSimpleProductIdFromDefaultSize(
    product: LegacyProduct,
    size: string,
  ) {
    if (product) {
      for (const id in product.children) {
        const child = product.children[id];

        if (child.shoeSize === size) {
          return child.id;
        }
      }
    }

    return '';
  }

  // Only used in SellingPageSwitcher - Doppelganger
  static graphHasRelationship(graph: Record<string, any>[], type: string) {
    return some(graph, (g) => g.attributes.relationship === type);
  }

  // Only used in SellingPageSwitcher - Doppelganger / Variants
  static filterGraph(graph: Record<string, any>[], type: string) {
    return filter(graph, (g) => g.attributes.relationship === type);
  }

  // Only used in Doppelganger pane - some duplication in meta data / shipping orders
  static getTrait(product: LegacyProduct, name: string) {
    if (product.traits && product.traits.length) {
      for (let i = 0, len = product.traits.length; i < len; i++) {
        if (
          product.traits[i].name &&
          product.traits[i].name?.toLowerCase() === name
        ) {
          return product.traits[i].value;
        }
      }
    }
  }

  // Only used below
  static getVerticalFromProduct(product?: LegacyProduct | undefined): Vertical {
    if (!product) {
      return VERTICALS.SNEAKERS as Vertical;
    }

    const { productCategory, primaryCategory } = product;
    if (productCategory?.toLowerCase() === 'collectibles') {
      return (primaryCategory || VERTICALS.COLLECTIBLES) as Vertical;
    }

    if (productCategory?.toLowerCase() === 'streetwear') {
      return VERTICALS.APPAREL as Vertical;
    }

    return (productCategory || VERTICALS.SNEAKERS) as Vertical;
  }

  // Used for Black Friday promo
  static getVerticalUrlFromProduct(product: LegacyProduct | undefined) {
    const vertical = this.getVerticalFromProduct(product);
    return vertical.replace(' ', '-').toLowerCase();
  }
}

export default Products;
